import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, PenLine, RotateCcw, X } from "lucide-react";
import { isChorus, splitSections } from "@/lib/lyricsSections";
import { safeApiError } from "@/lib/safeErrorText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ActionBtn({ testId, onClick, disabled, children }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} disabled={disabled}
      className="rounded-full border border-amber-300/35 bg-amber-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-50 transition-colors hover:bg-amber-500/20 disabled:opacity-40">
      {children}
    </button>
  );
}

function Preview({ result, stale, onUse, onRetry, onDismiss, busy }) {
  return (
    <div data-testid="cowriter-preview" className="mt-3 rounded-2xl border border-amber-300/30 bg-black/40 p-4">
      <p className="font-mono text-[10px] uppercase tracking-wider text-amber-200/80">ChatGPT suggests</p>
      <pre data-testid="cowriter-preview-text" className="mt-2 whitespace-pre-wrap font-body text-[14px] leading-relaxed text-white/90">{result.section}</pre>
      {result.note ? <p className="mt-2 text-[12px] text-white/55">{result.note}</p> : null}
      {stale ? <p data-testid="cowriter-stale" className="mt-2 text-[12px] text-amber-200">Your lyrics changed since this was written — try again to use it.</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" data-testid="cowriter-use" onClick={onUse} disabled={stale} className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 px-3.5 py-1.5 text-[12px] font-semibold text-[#05070f] hover:bg-amber-200 disabled:opacity-40"><Check className="h-3.5 w-3.5" /> Use this</button>
        <button type="button" data-testid="cowriter-retry" onClick={onRetry} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[12px] text-white/75 hover:bg-white/[0.08] disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" /> Try again</button>
        <button type="button" data-testid="cowriter-dismiss" onClick={onDismiss} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[12px] text-white/75 hover:bg-white/[0.08]"><X className="h-3.5 w-3.5" /> Keep mine</button>
      </div>
    </div>
  );
}

/** Gospel lyrics co-writer: pick a part of the song, ChatGPT rewrites it — nothing changes until you press "Use this". */
export default function LyricsCoWriter({ title, styleLabel, lyrics, onLyrics, disabled }) {
  const sections = useMemo(() => splitSections(lyrics).map((s, i) => ({ ...s, index: i })).filter((s) => s.header), [lyrics]);
  const [picked, setPicked] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [last, setLast] = useState(null);
  const target = sections.find((s) => s.index === picked) || null;

  const run = async (action, sectionIndex) => {
    setBusy(true);
    setLast({ action, sectionIndex });
    try {
      const { data } = await axios.post(`${API}/lyrics/refine`, {
        lyrics, action, section_index: sectionIndex, genre: styleLabel, title, instruction: instruction.trim(),
      }, { timeout: 150_000, withCredentials: true });
      setResult({ ...data, base: lyrics });
    } catch (e) { toast.error("ChatGPT couldn't write that part", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  const use = () => { onLyrics(result.lyrics); setResult(null); toast.success("Added to your lyrics"); };

  if (!lyrics.trim()) return null;
  return (
    <div data-testid="lyrics-cowriter" className="mt-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-amber-200/90"><PenLine className="h-4 w-4" /> Co-write with ChatGPT</p>
      <p className="mt-1 text-[12px] text-white/55">Pick a part to rewrite, or add a bridge. You see it first — nothing changes until you press “Use this”.</p>
      {sections.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Song parts">
          {sections.map((s) => (
            <button key={s.index} type="button" role="radio" aria-checked={picked === s.index} data-testid={`cowriter-section-${s.index}`} onClick={() => setPicked(picked === s.index ? null : s.index)}
              className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${picked === s.index ? "border-amber-300/70 bg-amber-500/20 text-white" : "border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.08]"}`}>
              {s.header}
            </button>
          ))}
        </div>
      ) : <p data-testid="cowriter-no-sections" className="mt-3 text-[12px] text-white/45">Add [Verse 1], [Chorus] style headers to pick a part.</p>}
      <input data-testid="cowriter-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={300}
        placeholder="What should change? (optional) — e.g. mention the Red Sea, more joyful"
        className="mt-3 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white placeholder:text-white/30 focus:border-amber-300/50 focus:outline-none" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {busy ? <span data-testid="cowriter-busy" className="inline-flex items-center gap-2 text-[12px] text-white/60"><Loader2 className="h-3.5 w-3.5 animate-spin" /> ChatGPT is writing…</span> : null}
        <ActionBtn testId="cowriter-rewrite" disabled={busy || disabled || !target} onClick={() => run("rewrite_section", target.index)}>Rewrite {target ? target.header : "a part"}</ActionBtn>
        {target && isChorus(target.header) ? <ActionBtn testId="cowriter-strengthen" disabled={busy || disabled} onClick={() => run("strengthen_chorus", target.index)}>Make the chorus stronger</ActionBtn> : null}
        {target ? <ActionBtn testId="cowriter-polish" disabled={busy || disabled} onClick={() => run("polish", target.index)}>Polish (keep my words)</ActionBtn> : null}
        <ActionBtn testId="cowriter-add-bridge" disabled={busy || disabled} onClick={() => run("add_bridge", null)}>Add a bridge</ActionBtn>
      </div>
      {result ? <Preview result={result} stale={result.base !== lyrics} busy={busy} onUse={use} onRetry={() => run(last.action, last.sectionIndex)} onDismiss={() => setResult(null)} /> : null}
    </div>
  );
}
